/**
 * Tipos del módulo Avisos. Espejan los schemas Pydantic del backend
 * (`schemas/aviso.py`). Mantener sincronizados si el backend cambia.
 */

export type AvisoTipo =
  | 'FIN_ENCARGO'
  | 'INMUEBLE_CAMPOS_FALTANTES'
  | 'OPERACION_CAMPOS_FALTANTES'
  | 'SUMINISTRO_PENDIENTE'
  | 'SEGURO_PENDIENTE'
  | 'MANUAL_LEAD';

export type AvisoOrigen = 'AUTOMATICO' | 'MANUAL';

export type AvisoEstado = 'PENDIENTE' | 'RESUELTO' | 'DESCARTADO';

export interface Aviso {
  id: number;
  tipo: AvisoTipo;
  origen: AvisoOrigen;

  titulo: string;
  descripcion?: string | null;

  inmueble_id?: number | null;
  operacion_id?: number | null;
  cambio_suministro_id?: number | null;
  seguro_alquiler_id?: number | null;
  lead_oficina_id?: number | null;
  lead_digital_id?: number | null;
  prospeccion_agente_id?: number | null;

  destinatario_user_id: number;
  creado_por_user_id?: number | null;

  fecha_evento?: string | null;
  fecha_visible_desde: string;
  antelacion_dias?: number | null;

  estado: AvisoEstado;
  fecha_creacion: string;
  fecha_resolucion?: string | null;
  fecha_lectura?: string | null;

  /** Nombre del lead/prospección asociado (si aplica), resuelto por backend. */
  lead_nombre?: string | null;
  /** Teléfono del lead/prospección asociado (si aplica), resuelto por backend. */
  lead_telefono?: string | null;

  /** Nombre del agente responsable (el destinatario), resuelto por backend. */
  destinatario_nombre?: string | null;
  /** Nombre de quien creó el aviso; null en los automáticos. */
  creado_por_nombre?: string | null;
}

/** Mapas { id_recurso → cuenta } por tabla, para los círculos rojos. */
export interface AvisoContadoresPorRecurso {
  inmuebles: Record<number, number>;
  operaciones: Record<number, number>;
  cambio_suministros: Record<number, number>;
  seguros_alquiler: Record<number, number>;
  leads_oficina: Record<number, number>;
  leads_digitales: Record<number, number>;
  prospeccion_agentes: Record<number, number>;
}

export interface AvisoContadores {
  total: number;
  por_tipo: Partial<Record<AvisoTipo, number>>;
  por_recurso: AvisoContadoresPorRecurso;
}

/** Payload para PATCH /avisos/{id}. Sólo avisos manuales; parcial. */
export interface AvisoUpdatePayload {
  titulo?: string;
  /** Único campo que admite null para vaciarse. */
  descripcion?: string | null;
  fecha_evento?: string;
  antelacion_dias?: number;
}

/** Payload para POST /avisos (creación manual desde seguimientos). */
export interface AvisoCreateManualPayload {
  titulo: string;
  descripcion?: string;
  fecha_evento: string;
  antelacion_dias?: number;
  lead_oficina_id?: number;
  lead_digital_id?: number;
  prospeccion_agente_id?: number;
}

/** Etiquetas legibles para mostrar en la UI. */
export const TIPO_LABELS: Record<AvisoTipo, string> = {
  FIN_ENCARGO: 'Fin de encargo próximo',
  INMUEBLE_CAMPOS_FALTANTES: 'Inmueble — datos pendientes',
  OPERACION_CAMPOS_FALTANTES: 'Operación — datos pendientes',
  SUMINISTRO_PENDIENTE: 'Suministro sin terminar',
  SEGURO_PENDIENTE: 'Seguro sin terminar',
  // El usuario final llama a estos «avisos de contacto»: los que crea un
  // agente para hacer una acción con un cliente. El valor del enum en BD
  // sigue siendo MANUAL_LEAD — renombrarlo pediría migración y tocar el
  // motor sin que el usuario gane nada.
  MANUAL_LEAD: 'Aviso de contacto',
};

export const ESTADO_LABELS: Record<AvisoEstado, string> = {
  PENDIENTE: 'Pendiente',
  RESUELTO: 'Resuelto',
  DESCARTADO: 'Descartado',
};
